/**
 * @file legal-acceptance.ts
 * @description Какие редакции обязательных документов принял покупатель при
 * заказе (phase19.16, SB143; проект данных
 * docs/phases/phase19.16-materials/db-design-legal-docs.md §1.2
 * `OrderLegalAcceptance`, §2.2 M2, §8): роли документа в заказе, единая форма
 * снимка `snapshot` и строка «Оферта: редакция N от даты» для ЛК и кабинета.
 *
 * Зачем в shared: снимок пишут чекаут (api) и миграция M2 (SQL
 * `jsonb_build_object`), а читают карточка заказа в кабинете и ЛК на витрине.
 * Снимок — источник правды (FK на редакцию — для аналитики), поэтому форма
 * одна и проверяется при чтении: битый снимок не роняет карточку заказа.
 *
 * Решение оркестратора 2026-10-19: редакции фиксируются в момент создания
 * заказа на сервере, без 409 (гонка «увидел N — записали N+1» — TD).
 */

import { z } from "zod";

import { deepFreeze } from "../site-theme/deep-freeze.js";
import { legalDocumentHref, legalRevisionHref, type LegalSystemSlug } from "./legal-slugs.js";

/** Роль документа в заказе (= enum `OrderLegalDocRole`). */
export const LEGAL_ACCEPTANCE_ROLES = deepFreeze(["OFFER", "PRIVACY_POLICY", "RETURNS"] as const);
export type LegalAcceptanceRole = (typeof LEGAL_ACCEPTANCE_ROLES)[number];

/** Адрес документа по роли. */
export const LEGAL_ACCEPTANCE_ROLE_SLUGS: Readonly<Record<LegalAcceptanceRole, LegalSystemSlug>> = deepFreeze({
  OFFER: "offer",
  PRIVACY_POLICY: "privacy-policy",
  RETURNS: "returns-notice",
});

/** Подписи ролей: «Оферта: редакция 4 от 12.10.2026». */
export const LEGAL_ACCEPTANCE_ROLE_LABELS: Readonly<Record<LegalAcceptanceRole, string>> = deepFreeze({
  OFFER: "Оферта",
  PRIVACY_POLICY: "Политика ПДн",
  RETURNS: "Условия возврата",
});

const sha256 = z.string().regex(/^[0-9a-f]{64}$/);

/**
 * Снимок принятой редакции (`OrderLegalAcceptance.snapshot`). `backfilled` —
 * строка заполнена переносом M2 по дате заказа, а не записана чекаутом.
 */
export const legalAcceptanceSnapshotSchema = z.object({
  documentId: z.string().min(1),
  slug: z.string().min(1),
  href: z.string().startsWith("/"),
  title: z.string(),
  number: z.number().int().min(1),
  /** ISO-строка в UTC. */
  publishedAt: z.string().min(1),
  contentSha256: sha256,
  /** Только PDF: sha256 файла (= contentSha256). */
  fileSha256: sha256.optional(),
  backfilled: z.literal(true).optional(),
});
export type LegalAcceptanceSnapshot = z.infer<typeof legalAcceptanceSnapshotSchema>;

/** Поля редакции, из которых строится снимок. */
export interface LegalAcceptanceRevisionInput {
  documentId: string;
  slug: string;
  title: string;
  number: number;
  publishedAt: Date | string;
  contentSha256: string;
  fileSha256?: string | null;
}

/**
 * Дата в ISO UTC. Строка без пояса (`jsonb_build_object` от TIMESTAMP(3)
 * в M2 даёт «2026-10-20T10:00:00.123») — это UTC, как все даты Prisma.
 */
function isoUtc(value: Date | string): string | null {
  const text = typeof value === "string" && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? `${value}Z` : value;
  const date = text instanceof Date ? text : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Снимок принятой редакции — одна форма для чекаута и тестов M2. Адрес —
 * адрес документа на сайте (`/offer`, `/privacy`, `/legal/<адрес>`).
 * Битая дата или хеш — ошибка вызывающего (fail fast: пишем доказательство).
 */
export function legalAcceptanceSnapshot(
  rev: LegalAcceptanceRevisionInput,
  options: { backfilled?: boolean } = {},
): LegalAcceptanceSnapshot {
  const publishedAt = isoUtc(rev.publishedAt);
  if (publishedAt === null) throw new RangeError(`legal_acceptance_date_invalid: ${String(rev.publishedAt)}`);
  const snapshot: LegalAcceptanceSnapshot = {
    documentId: rev.documentId,
    slug: rev.slug,
    href: legalDocumentHref(rev.slug),
    title: rev.title,
    number: rev.number,
    publishedAt,
    contentSha256: rev.contentSha256,
  };
  if (rev.fileSha256) snapshot.fileSha256 = rev.fileSha256;
  if (options.backfilled) snapshot.backfilled = true;
  return legalAcceptanceSnapshotSchema.parse(snapshot);
}

/** Чтение сохранённого снимка: дата приводится к ISO UTC; битый — null (карточка заказа не падает). */
export function readLegalAcceptanceSnapshot(raw: unknown): LegalAcceptanceSnapshot | null {
  const parsed = legalAcceptanceSnapshotSchema.safeParse(raw);
  if (!parsed.success) return null;
  const publishedAt = isoUtc(parsed.data.publishedAt);
  return publishedAt === null ? null : { ...parsed.data, publishedAt };
}

/** Строка «Документы, принятые при заказе» в ЛК и карточке заказа. */
export interface LegalAcceptanceDto {
  role: LegalAcceptanceRole;
  /** «Оферта», «Политика ПДн», «Условия возврата». */
  label: string;
  title: string;
  number: number;
  publishedAt: string;
  /** Страница этой редакции: `/legal/<адрес>/revisions/<N>`. */
  href: string;
  backfilled: boolean;
}

/** Строка показа из снимка; битый снимок — null (строку не показываем). */
export function legalAcceptanceView(role: LegalAcceptanceRole, raw: unknown): LegalAcceptanceDto | null {
  const snapshot = readLegalAcceptanceSnapshot(raw);
  if (snapshot === null) return null;
  return {
    role,
    label: LEGAL_ACCEPTANCE_ROLE_LABELS[role],
    title: snapshot.title,
    number: snapshot.number,
    publishedAt: snapshot.publishedAt,
    href: legalRevisionHref(snapshot.slug, snapshot.number),
    backfilled: snapshot.backfilled === true,
  };
}
